import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Search, FileText, Download, User, Baby, Heart, Activity, Calendar, ArrowUpDown, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { reportService } from '@/services/reportService';
import { PatientClinicalSummary } from '@/types/report.types';
import { patientService } from '@/services/patientService';
import { Patient } from '@/types/patient.types';

type SearchType = 'cnp' | 'name';

type TimelineEvent = {
    date: string;
    type: 'birth' | 'diagnosis' | 'procedure';
    icon: React.ReactNode;
    title: string;
    description: string;
    metadata?: any;
    color: string;
};

type EventFilter = 'all' | 'diagnosis' | 'procedure';

export const PatientClinicalSummaryReport: React.FC = () => {
    const [searchType, setSearchType] = useState<SearchType>('cnp');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<PatientClinicalSummary | null>(null);
    const [searchResults, setSearchResults] = useState<Patient[]>([]);
    const [timelineSortOrder, setTimelineSortOrder] = useState<'asc' | 'desc'>('asc');
    const [eventFilter, setEventFilter] = useState<EventFilter>('all');
    const [diagnosesExpanded, setDiagnosesExpanded] = useState(true);
    const [proceduresExpanded, setProceduresExpanded] = useState(true);

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setError('Introduceți termenul de căutare');
            return;
        }

        setSearching(true);
        setError(null);
        setSearchResults([]);
        setSummary(null);

        try {
            if (searchType === 'cnp') {
                // For CNP, search and directly load summary if found
                const results = await patientService.searchPatients(searchTerm.trim());

                if (results.length === 0) {
                    setError('Nu a fost găsit niciun pacient cu acest CNP');
                    return;
                }

                // If exactly one result, load summary directly
                if (results.length === 1) {
                    await loadSummaryForPatient(results[0].id);
                } else {
                    // Multiple results, show them
                    setSearchResults(results);
                }
            } else {
                // For name, search and show results
                const results = await patientService.searchPatients(searchTerm.trim());

                if (results.length === 0) {
                    setError('Nu a fost găsit niciun pacient cu acest nume');
                    return;
                }

                setSearchResults(results);
            }
        } catch (err: any) {
            setError(err.message || 'Eroare la căutarea pacientului');
        } finally {
            setSearching(false);
        }
    };

    const loadSummaryForPatient = async (patientId: number) => {
        setLoading(true);
        setError(null);

        try {
            const data = await reportService.getPatientClinicalSummary(patientId);
            setSummary(data);
            setSearchResults([]);
        } catch (err: any) {
            setError(err.message || 'Eroare la încărcarea rezumatului clinic');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelect = (patient: Patient) => {
        loadSummaryForPatient(patient.id);
    };

    const handleReset = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSummary(null);
        setError(null);
    };

    const getSeverityLabel = (level: number) => {
        const labels = ['', 'Ușoară', 'Moderată', 'Severă', 'Critică', 'Foarte critică'];
        return labels[level] || 'Necunoscut';
    };

    const getSeverityColor = (level: number) => {
        const colors = ['', 'bg-green-100 text-green-800', 'bg-yellow-100 text-yellow-800',
            'bg-orange-100 text-orange-800', 'bg-red-100 text-red-800', 'bg-red-200 text-red-900'];
        return colors[level] || 'bg-gray-100 text-gray-800';
    };

    const getPlaceholder = () => {
        if (searchType === 'cnp') {
            return 'Introduceți CNP-ul pacientului (13 cifre)';
        }
        return 'Introduceți numele pacientului';
    };

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        const months = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'noi', 'dec'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const timelineEvents = useMemo<TimelineEvent[]>(() => {
        if (!summary) return [];

        const events: TimelineEvent[] = [];

        // Birth event
        events.push({
            date: summary.patient.birth_date,
            type: 'birth',
            icon: <Baby className="h-5 w-5" />,
            title: 'Naștere',
            description: `Născut la ${formatDate(summary.patient.birth_date)}`,
            metadata: {
                birth_weight: summary.patient.birth_weight,
                gestational_age: summary.patient.gestational_age
            },
            color: 'blue'
        });

        // Diagnosis events
        summary.diagnoses.forEach((diagnosis) => {
            events.push({
                date: diagnosis.diagnosis_date,
                type: 'diagnosis',
                icon: <Heart className="h-5 w-5" />,
                title: `Diagnostic: ${diagnosis.icd10_code}`,
                description: diagnosis.description_ro,
                metadata: {
                    is_primary: diagnosis.is_primary,
                    severity_level: diagnosis.severity_level,
                    severity_name: diagnosis.severity_name
                },
                color: 'red'
            });
        });

        // Procedure events
        summary.procedures.forEach((procedure) => {
            events.push({
                date: procedure.procedure_date,
                type: 'procedure',
                icon: <Activity className="h-5 w-5" />,
                title: `Procedură: ${procedure.procedure_type_name}`,
                description: procedure.procedure_type_name,
                metadata: {
                    outcome_30d: procedure.outcome_30d,
                    complications: procedure.complications,
                    reintervention_1y: procedure.reintervention_1y
                },
                color: 'green'
            });
        });

        // Sort by date
        events.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return timelineSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });

        // Apply filter
        if (eventFilter !== 'all') {
            return events.filter(e => e.type === eventFilter || e.type === 'birth');
        }

        return events;
    }, [summary, timelineSortOrder, eventFilter]);

    const toggleSortOrder = () => {
        setTimelineSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const getEventColorClasses = (color: string) => {
        const colorMap: Record<string, { bg: string; border: string; text: string; dot: string }> = {
            blue: {
                bg: 'bg-blue-50',
                border: 'border-blue-300',
                text: 'text-blue-700',
                dot: 'bg-blue-500'
            },
            red: {
                bg: 'bg-red-50',
                border: 'border-red-300',
                text: 'text-red-700',
                dot: 'bg-red-500'
            },
            green: {
                bg: 'bg-green-50',
                border: 'border-green-300',
                text: 'text-green-700',
                dot: 'bg-green-500'
            }
        };
        return colorMap[color] || colorMap.blue;
    };

    return (
        <div className="space-y-6">
            {/* Search Form */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Căutare pacient</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex gap-4 items-end">
                            <div className="w-48">
                                <Label htmlFor="searchType">Tip căutare</Label>
                                <Select value={searchType} onValueChange={(value: SearchType) => {
                                    setSearchType(value);
                                    setSearchTerm('');
                                    setSearchResults([]);
                                    setError(null);
                                }}>
                                    <SelectTrigger id="searchType">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cnp">CNP</SelectItem>
                                        <SelectItem value="name">Nume</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex-1">
                                <Label htmlFor="searchTerm">
                                    {searchType === 'cnp' ? 'CNP' : 'Nume'}
                                </Label>
                                <Input
                                    id="searchTerm"
                                    type="text"
                                    placeholder={getPlaceholder()}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <Button onClick={handleSearch} disabled={searching || loading}>
                                {searching ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Search className="h-4 w-4 mr-2" />
                                )}
                                Căutare
                            </Button>
                            {(summary || searchResults.length > 0) && (
                                <Button variant="outline" onClick={handleReset}>
                                    Resetare
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Rezultate căutare ({searchResults.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {searchResults.map((patient) => (
                                <Card
                                    key={patient.id}
                                    className="cursor-pointer hover:bg-accent transition-colors"
                                    onClick={() => handlePatientSelect(patient)}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                                            <div className="flex-1 space-y-1">
                                                <p className="font-medium">{patient.name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Cod: {patient.patient_code}
                                                </p>
                                                {patient.cnp && (
                                                    <p className="text-sm text-muted-foreground">
                                                        CNP: {patient.cnp}
                                                    </p>
                                                )}
                                                <p className="text-sm text-muted-foreground">
                                                    Data nașterii: {new Date(patient.birth_date).toLocaleDateString('ro-RO')}
                                                </p>
                                                {patient.hospital_name && (
                                                    <p className="text-sm text-muted-foreground">
                                                        Spital: {patient.hospital_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {summary && (
                <div className="space-y-4">
                    {/* Patient Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Informații pacient</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Nume</p>
                                    <p className="font-medium">{summary.patient.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Cod</p>
                                    <p className="font-medium">{summary.patient.patient_code}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Data nașterii</p>
                                    <p className="font-medium">{new Date(summary.patient.birth_date).toLocaleDateString('ro-RO')}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Spital</p>
                                    <p className="font-medium">{summary.patient.hospital_name}</p>
                                </div>
                                {summary.patient.birth_weight && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Greutate naștere</p>
                                        <p className="font-medium">{summary.patient.birth_weight}g</p>
                                    </div>
                                )}
                                {summary.patient.gestational_age && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Vârstă gestațională</p>
                                        <p className="font-medium">{summary.patient.gestational_age} săpt</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Patient Timeline */}
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <CardTitle>Cronologie pacient</CardTitle>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={toggleSortOrder}
                                    >
                                        <ArrowUpDown className="h-4 w-4 mr-2" />
                                        {timelineSortOrder === 'asc' ? 'Cele mai vechi' : 'Cele mai recente'}
                                    </Button>
                                    <Select value={eventFilter} onValueChange={(value: EventFilter) => setEventFilter(value)}>
                                        <SelectTrigger className="w-full sm:w-[180px]">
                                            <Filter className="h-4 w-4 mr-2" />
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toate evenimentele</SelectItem>
                                            <SelectItem value="diagnosis">Doar diagnostice</SelectItem>
                                            <SelectItem value="procedure">Doar proceduri</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {timelineEvents.length > 0 ? (
                                <div className="relative space-y-6">
                                    {/* Vertical timeline line */}
                                    <div className="absolute left-[28px] top-6 bottom-6 w-0.5 bg-gray-200" />

                                    {timelineEvents.map((event, index) => {
                                        const colors = getEventColorClasses(event.color);
                                        return (
                                            <div key={index} className="relative flex gap-4">
                                                {/* Timeline dot and icon */}
                                                <div className="flex flex-col items-center">
                                                    <div className={`flex items-center justify-center w-14 h-14 rounded-full ${colors.bg} border-2 ${colors.border} z-10`}>
                                                        <div className={colors.text}>
                                                            {event.icon}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Event content */}
                                                <div className="flex-1 pb-6">
                                                    <div className={`p-4 rounded-lg border ${colors.border} ${colors.bg} transition-all hover:shadow-md`}>
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                                                    <span className="text-sm font-medium text-muted-foreground">
                                                                        {formatDate(event.date)}
                                                                    </span>
                                                                </div>
                                                                <h4 className={`font-semibold ${colors.text}`}>{event.title}</h4>
                                                            </div>
                                                            <Badge variant="outline" className={`${colors.text} border-current`}>
                                                                {event.type === 'birth' && 'Naștere'}
                                                                {event.type === 'diagnosis' && 'Diagnostic'}
                                                                {event.type === 'procedure' && 'Procedură'}
                                                            </Badge>
                                                        </div>

                                                        <p className="text-sm text-gray-700 mb-3">{event.description}</p>

                                                        {/* Event metadata */}
                                                        {event.type === 'birth' && event.metadata && (
                                                            <div className="flex gap-4 text-sm">
                                                                {event.metadata.birth_weight && (
                                                                    <span className="text-muted-foreground">
                                                                        <strong>Greutate:</strong> {event.metadata.birth_weight}g
                                                                    </span>
                                                                )}
                                                                {event.metadata.gestational_age && (
                                                                    <span className="text-muted-foreground">
                                                                        <strong>Vârsta gestațională:</strong> {event.metadata.gestational_age} săpt
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {event.type === 'diagnosis' && event.metadata && (
                                                            <div className="flex gap-2 flex-wrap">
                                                                {event.metadata.is_primary && (
                                                                    <Badge variant="default">Primar</Badge>
                                                                )}
                                                                <Badge className={getSeverityColor(event.metadata.severity_level)}>
                                                                    {event.metadata.severity_name}
                                                                </Badge>
                                                            </div>
                                                        )}

                                                        {event.type === 'procedure' && event.metadata && (
                                                            <div className="space-y-1 text-sm">
                                                                {event.metadata.outcome_30d && (
                                                                    <p>
                                                                        <span className="text-muted-foreground">Rezultat 30 zile:</span>{' '}
                                                                        <span className="font-medium">{event.metadata.outcome_30d}</span>
                                                                    </p>
                                                                )}
                                                                {event.metadata.complications && (
                                                                    <p>
                                                                        <span className="text-muted-foreground">Complicații:</span>{' '}
                                                                        <span className="font-medium">{event.metadata.complications}</span>
                                                                    </p>
                                                                )}
                                                                {event.metadata.reintervention_1y && (
                                                                    <p>
                                                                        <span className="text-muted-foreground">Reintervenție 1 an:</span>{' '}
                                                                        <span className="font-medium">{event.metadata.reintervention_1y}</span>
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-8">
                                    Nu există evenimente în cronologie
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Diagnoses - Collapsible */}
                    <Card>
                        <CardHeader>
                            <div
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => setDiagnosesExpanded(!diagnosesExpanded)}
                            >
                                <CardTitle className="flex items-center gap-2">
                                    <Heart className="h-5 w-5" />
                                    Diagnostice ({summary.diagnoses.length})
                                </CardTitle>
                                <Button variant="ghost" size="sm">
                                    {diagnosesExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        {diagnosesExpanded && (
                            <CardContent>
                                {summary.diagnoses.length > 0 ? (
                                    <div className="space-y-2">
                                        {summary.diagnoses.map((diagnosis) => (
                                            <div key={diagnosis.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium">{diagnosis.icd10_code}</p>
                                                        {diagnosis.is_primary && (
                                                            <Badge variant="default">Primar</Badge>
                                                        )}
                                                        <Badge className={getSeverityColor(diagnosis.severity_level)}>
                                                            {getSeverityLabel(diagnosis.severity_level)}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground mt-1">{diagnosis.description_ro}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Data: {new Date(diagnosis.diagnosis_date).toLocaleDateString('ro-RO')}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Nu există diagnostice înregistrate
                                    </p>
                                )}
                            </CardContent>
                        )}
                    </Card>

                    {/* Procedures - Collapsible */}
                    <Card>
                        <CardHeader>
                            <div
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => setProceduresExpanded(!proceduresExpanded)}
                            >
                                <CardTitle className="flex items-center gap-2">
                                    <Activity className="h-5 w-5" />
                                    Proceduri ({summary.procedures.length})
                                </CardTitle>
                                <Button variant="ghost" size="sm">
                                    {proceduresExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        {proceduresExpanded && (
                            <CardContent>
                                {summary.procedures.length > 0 ? (
                                    <div className="space-y-2">
                                        {summary.procedures.map((procedure) => (
                                            <div key={procedure.id} className="p-3 border rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-medium">{procedure.procedure_type_name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {new Date(procedure.procedure_date).toLocaleDateString('ro-RO')}
                                                    </p>
                                                </div>
                                                {procedure.outcome_30d && (
                                                    <p className="text-sm mt-1">
                                                        <span className="text-muted-foreground">Rezultat 30 zile:</span> {procedure.outcome_30d}
                                                    </p>
                                                )}
                                                {procedure.complications && (
                                                    <p className="text-sm mt-1">
                                                        <span className="text-muted-foreground">Complicații:</span> {procedure.complications}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Nu există proceduri înregistrate
                                    </p>
                                )}
                            </CardContent>
                        )}
                    </Card>

                    {/* VON Completeness */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Completitudine date RONN</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Secțiuni completate</span>
                                    <span className="font-medium">
                                        {summary.von_completeness.completed_sections} / {summary.von_completeness.total_sections}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-green-600 h-2 rounded-full transition-all"
                                        style={{ width: `${summary.von_completeness.percentage}%` }}
                                    />
                                </div>
                                <p className="text-sm text-center font-medium">
                                    {summary.von_completeness.percentage.toFixed(1)}% completare
                                </p>
                                {summary.von_completeness.missing_sections.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm text-muted-foreground mb-2">Secțiuni incomplete:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {summary.von_completeness.missing_sections.map((section) => (
                                                <Badge key={section} variant="outline">{section}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Export Button */}
                    <div className="flex justify-end">
                        <Button>
                            <Download className="h-4 w-4 mr-2" />
                            Export PDF
                        </Button>
                    </div>
                </div>
            )}

            {!summary && !loading && !searching && searchResults.length === 0 && (
                <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                        Căutați un pacient după CNP sau nume pentru a vizualiza rezumatul clinic
                    </p>
                </div>
            )}
        </div>
    );
};
